import {
  ACTION_BAR_HEIGHT_VAR,
  TOAST_STACK_HEIGHT_VAR,
  alertIcons,
  alertToneText,
} from '@/design-system'
import { useI18n } from '@/i18n'
import { X } from '@/shared/icons/phosphor'
import {
  type Component,
  For,
  type JSX,
  Show,
  createContext,
  createEffect,
  createSignal,
  on,
  onCleanup,
  onMount,
  useContext,
} from 'solid-js'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface ToastAction {
  label: string
  onAction: () => void
}

export interface Toast {
  id: string
  type: ToastType
  message: string
  duration?: number
  action?: ToastAction
}

interface ToastContextValue {
  toasts: () => Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => string
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue>()

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: JSX.Element
}

export const ToastProvider: Component<ToastProviderProps> = (props) => {
  const [toasts, setToasts] = createSignal<Toast[]>([])

  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).slice(2, 9)
    const newToast: Toast = { ...toast, id }
    setToasts((prev) => [...prev, newToast])

    const duration = toast.duration ?? 5000
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }
    return id
  }

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {props.children}
    </ToastContext.Provider>
  )
}

const typeStyles: Record<ToastType, string> = {
  success:
    'border-[var(--color-success-200)] bg-[var(--color-success-50)] text-[color:var(--color-text)]',
  error:
    'border-[var(--color-error-200)] bg-[var(--color-error-50)] text-[color:var(--color-text)]',
  warning:
    'border-[var(--color-warning-200)] bg-[var(--color-warning-50)] text-[color:var(--color-text)]',
  info: 'border-[var(--color-info-200)] bg-[var(--color-info-50)] text-[color:var(--color-text)]',
}

const announcement: Record<ToastType, 'assertive' | 'polite'> = {
  success: 'polite',
  error: 'assertive',
  warning: 'assertive',
  info: 'polite',
}

const isAssertive = (toast: Toast) => announcement[toast.type] === 'assertive'

const STACK_BOTTOM_OFFSET = `var(${ACTION_BAR_HEIGHT_VAR}, 0px) + 1rem`

const mountedStacks = new Set<HTMLElement>()

const publishStackHeight = () => {
  let tallest = 0
  for (const stack of mountedStacks) {
    tallest = Math.max(tallest, stack.offsetHeight)
  }
  if (tallest === 0) {
    document.documentElement.style.removeProperty(TOAST_STACK_HEIGHT_VAR)
    return
  }
  document.documentElement.style.setProperty(
    TOAST_STACK_HEIGHT_VAR,
    `calc(${tallest}px + ${STACK_BOTTOM_OFFSET})`
  )
}

const ToastItem: Component<{ toast: Toast; onClose: () => void; onActionDone: () => void }> = (
  props
) => {
  const { t } = useI18n()
  const messageId = `toast-message-${props.toast.id}`
  const layout = () =>
    props.toast.action
      ? { toast: 'flex-wrap gap-x-4 gap-y-2', message: 'basis-[max-content]' }
      : { toast: 'gap-3', message: 'flex-1' }
  const TypeIcon = alertIcons[props.toast.type]

  return (
    <div
      data-toast
      class={`
        pointer-events-auto w-full max-w-md
        flex items-start ${layout().toast}
        px-4 py-3 rounded-[var(--radius-lg)] border
        shadow-[var(--shadow-lg)]
        animate-scale-in
        ${typeStyles[props.toast.type]}
      `}
    >
      <div class={`flex min-w-0 items-start gap-3 ${layout().message}`}>
        <TypeIcon
          data-toast-icon
          aria-hidden="true"
          class={`mt-0.5 h-4 w-4 shrink-0 ${alertToneText[props.toast.type]}`}
        />
        <p
          id={messageId}
          role={isAssertive(props.toast) ? 'alert' : undefined}
          class="min-w-0 flex-1 wrap-anywhere text-pretty text-[length:var(--text-sm)] font-[var(--font-medium)]"
        >
          {props.toast.message}
        </p>
      </div>
      <div class="ml-auto flex h-[1lh] shrink-0 items-center gap-2 text-[length:var(--text-sm)]">
        <Show when={props.toast.action}>
          {(action) => (
            <button
              type="button"
              data-toast-action
              aria-describedby={messageId}
              onClick={() => {
                action().onAction()
                props.onActionDone()
              }}
              class="
                shrink-0 px-2 py-0.5 rounded-md
                text-[length:var(--text-sm)] font-[var(--font-semibold)]
                underline underline-offset-2 hover:decoration-2
                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current
              "
            >
              {action().label}
            </button>
          )}
        </Show>
        <button
          type="button"
          data-toast-close
          onClick={props.onClose}
          class="p-1 rounded-md hover:bg-[color-mix(in_srgb,currentColor_20%,transparent)] transition-colors"
          aria-label={t('common.close')}
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

export const ToastContainer: Component = () => {
  const { toasts, removeToast } = useToast()
  const politeToasts = () => toasts().filter((toast) => !isAssertive(toast))
  let stack: HTMLDivElement | undefined

  const removeAfterAction = (id: string) => {
    const position = toasts().findIndex((toast) => toast.id === id)
    removeToast(id)
    if (!stack || (document.activeElement && document.activeElement !== document.body)) {
      return
    }
    const remaining = stack.querySelectorAll<HTMLElement>('[data-toast]')
    const next = remaining[Math.min(position, remaining.length - 1)]
    next?.querySelector<HTMLElement>('[data-toast-action], [data-toast-close]')?.focus()
  }

  onMount(() => {
    const element = stack
    if (!element) {
      return
    }
    mountedStacks.add(element)
    onCleanup(() => {
      mountedStacks.delete(element)
      publishStackHeight()
    })

    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(publishStackHeight)
    observer.observe(element)
    onCleanup(() => observer.disconnect())
  })

  createEffect(on(toasts, publishStackHeight))

  return (
    <div
      ref={stack}
      // Kobalte's ariaHideOutside spares this marker; without it a modal dialog hides every toast from screen readers.
      data-live-announcer="true"
      data-toast-stack
      class="pointer-events-none fixed z-[60] flex flex-col items-center gap-2 px-4"
      style={{
        bottom: `calc(${STACK_BOTTOM_OFFSET})`,
        left: 'var(--app-content-left, 0px)',
        right: 'var(--app-content-right, 0px)',
      }}
    >
      <div role="status" aria-live="polite" aria-atomic="false" class="sr-only">
        <For each={politeToasts()}>{(toast) => <p>{toast.message}</p>}</For>
      </div>
      <For each={toasts()}>
        {(toast) => (
          <ToastItem
            toast={toast}
            onClose={() => removeToast(toast.id)}
            onActionDone={() => removeAfterAction(toast.id)}
          />
        )}
      </For>
    </div>
  )
}
